import React from 'react';
import BaseInput from '../ui/BaseInput';
import ErrorBanner from '../ui/ErrorBanner';
import { SignUpFormData } from '../../types/auth';

interface SignUpFormProps {
  onSubmit: (data: SignUpFormData) => void;
  loading?: boolean;
  onSwitchToLogin?: () => void;
  fieldErrors?: Record<string, string>;
  generalError?: string | null;
  feedbackBelowButton?: React.ReactNode;
}

const FUNCTION_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Select your function' },
  { value: 'Developer', label: 'Developer' },
  { value: 'Designer', label: 'Designer' },
  { value: 'Product Manager', label: 'Product Manager' },
  { value: 'Data Scientist', label: 'Data Scientist' },
  { value: 'Marketing', label: 'Marketing' },
  { value: 'Other', label: 'Other' },
];

const SignUpForm = (props: SignUpFormProps) => {
  const { onSubmit, loading, onSwitchToLogin, fieldErrors = {}, generalError } = props;
  const [formData, setFormData] = React.useState<SignUpFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    function: '',
  });

  const handleChange = (field: keyof SignUpFormData) => (value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, function: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {generalError && (
        <ErrorBanner
          title="Registration Failed"
          message={generalError}
          type="error"
          action={onSwitchToLogin ? { text: 'Try logging in instead', onClick: onSwitchToLogin } : undefined}
        />
      )}
      <BaseInput
        label="First Name"
        name="firstName"
        type="text"
        value={formData.firstName}
        onChange={e => handleChange('firstName')(e)}
        error={fieldErrors.firstName}
        required
        placeholder="First Name"
        autoComplete="given-name"
      />
      <BaseInput
        label="Last Name"
        name="lastName"
        type="text"
        value={formData.lastName}
        onChange={e => handleChange('lastName')(e)}
        error={fieldErrors.lastName}
        required
        placeholder="Last Name"
        autoComplete="family-name"
      />
      <BaseInput
        label="Email Address"
        name="email"
        type="email"
        value={formData.email}
        onChange={e => handleChange('email')(e)}
        error={fieldErrors.email}
        required
        placeholder="you@company.com"
        autoComplete="email"
      />
      <BaseInput
        label="Password"
        name="password"
        type="password"
        value={formData.password}
        onChange={e => handleChange('password')(e)}
        error={fieldErrors.password}
        required
        placeholder="Password"
        autoComplete="new-password"
      />
      <BaseInput
        label="Confirm Password"
        name="confirmPassword"
        type="password"
        value={formData.confirmPassword}
        onChange={e => handleChange('confirmPassword')(e)}
        error={fieldErrors.confirmPassword}
        required
        placeholder="Confirm Password"
        autoComplete="new-password"
      />
      <div className="space-y-1">
        <select
          name="function"
          value={formData.function}
          onChange={handleSelectChange}
          required
          className={`w-full px-4 py-3 rounded-md border transition-all duration-200 ease-out appearance-none focus:outline-none focus:ring-1 focus:ring-brand-electric-blue focus:border-brand-electric-blue ${
            fieldErrors.function
              ? 'border-red-500 text-red-900 focus:border-red-500 focus:ring-red-500'
              : 'border-gray-300'
          }`}
          aria-invalid={!!fieldErrors.function}
          aria-describedby={fieldErrors.function ? 'function-error' : undefined}
        >
          {FUNCTION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value} disabled={option.value === ''}>
              {option.label}
            </option>
          ))}
        </select>
        <div
          className={`overflow-hidden transition-all duration-200 ease-out ${
            fieldErrors.function ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
          }`}
          id={fieldErrors.function ? 'function-error' : undefined}
          aria-live="polite"
        >
          {fieldErrors.function && (
            <div className="flex items-center space-x-2 mt-2 text-sm text-red-600">
              <svg className="h-4 w-4 flex-shrink-0 animate-bounce-gentle text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" /></svg>
              <span>{fieldErrors.function}</span>
            </div>
          )}
        </div>
      </div>
      <button
        type="submit"
        disabled={loading}
        className={`w-full bg-brand-dark-blue hover:bg-brand-electric-blue disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-md transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:ring-offset-2 ${loading ? 'cursor-not-allowed' : ''}`}
      >
        {loading ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Creating account...</span>
          </div>
        ) : (
          'Create Account'
        )}
      </button>
      {props.feedbackBelowButton}
      <p className="text-center text-sm text-gray-600 mt-2">
        Already have an account?{' '}
        <a href="#" className="text-brand-electric-blue hover:text-brand-dark-blue" onClick={(e) => {
          e.preventDefault();
          onSwitchToLogin?.();
        }}>
          Log In
        </a>
      </p>
    </form>
  );
};

export { SignUpForm };